'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Blockly from 'blockly/core';
import * as Ja from 'blockly/msg/ja';
import type { FunctionDef } from '@/stores/useBlockFunctionStore';
import type { FunctionParam } from '@/stores/useBlockFunctionStore';
import { useBlockFunctionStore } from '@/stores/useBlockFunctionStore';
import { useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import '@/(routers)/boards/editor/block/block.css';
import { isChangingBoardFromBlock, syncAllBoardBlocks } from '@/(routers)/boards/editor/block/blockFactory';
import { loadWorkspace } from '@/(routers)/boards/editor/block/blockLoader';
import {
    captureDeleteCandidate,
    disposeBlockParticle,
    initBlockParticle,
    triggerDeleteEffect,
} from '@/(routers)/boards/editor/block/blockParticle';
import '@/(routers)/boards/editor/block/blockRenderer';
import { blockSound } from '@/(routers)/boards/editor/block/blockSound';
import { isShadowSoundSuppressed } from '@/(routers)/boards/editor/block/blockSoundControl';
import { schoomyTheme } from '@/(routers)/boards/editor/block/blockTheme';
import {
    validateBoardBlocksUnder,
    validateDeviceNameBlocksUnder,
    validateFuncReturnBlocksUnder,
    validateI2CDevices,
    validateLoopControlBlocksUnder,
} from '@/(routers)/boards/editor/block/blockValidator';
import '@/(routers)/boards/editor/block/definitions';
import { registerBoolPreset } from '@/(routers)/boards/editor/block/definitions/blocks/variable/var_get_bool_preset';
import { registerNumberPreset } from '@/(routers)/boards/editor/block/definitions/blocks/variable/var_get_number_preset';
import { registerStringPreset } from '@/(routers)/boards/editor/block/definitions/blocks/variable/var_get_string_preset';
import { emptyFlyout, patchFlyoutNoDelete } from '@/(routers)/boards/editor/block/definitions/flyout';
import { initBlockMenu } from '@/(routers)/boards/editor/block/definitions/menu';
import '@/(routers)/boards/editor/block/generators';
import { generateArduinoCode } from '@/(routers)/boards/editor/block/generators/arduino';
import BlockModal from '@/(routers)/boards/editor/block/modal/BlockModal';
import {
    applyFlyoutLayout,
    forceFlyoutBackgroundHardRect,
} from '@/(routers)/boards/editor/components/unit/BoardBlockEditorCategory';
import BoardBlockEditorCategory from '@/(routers)/boards/editor/components/unit/BoardBlockEditorCategory';
import BoardBlockEditorControls from '@/(routers)/boards/editor/components/unit/BoardBlockEditorControls';
import BoardBlockSyncBanner from '@/(routers)/boards/editor/components/unit/BoardBlockSyncBanner';

type SoundName = 'drag-start' | 'drag-end' | 'connect' | 'disconnect' | 'delete';

const CONNECT_SUPPRESS_MS = 160;
const CODE_GEN_DEBOUNCE_MS = 150;
const DRAG_END_DELAY_MS = 25;

function buildSafePlay() {
    const lastPlayAt = new Map<string, number>();
    return function safePlay(_name: SoundName, key: string, minIntervalMs: number) {
        const now = performance.now();
        if ((lastPlayAt.get(key) ?? 0) + minIntervalMs > now) return;
        lastPlayAt.set(key, now);
        blockSound.queue(_name);
    };
}

function buildChangeListener(
    ws: Blockly.WorkspaceSvg,
    isLoadingRef: React.RefObject<boolean>,
    isDisposingRef: React.RefObject<boolean>,
    setBlockZoomScale: (scale: number) => void,
): { listener: (e: Blockly.Events.Abstract) => void; cancelCodeGen: () => void; cancelDragEnd: () => void } {
    const safePlay = buildSafePlay();

    let dragEndTimer: ReturnType<typeof setTimeout> | null = null;
    let codeGenTimer: ReturnType<typeof setTimeout> | null = null;
    let lastConnectLikeAt = 0;
    let isDragging = false;

    function cancelDragEnd() {
        if (dragEndTimer) {
            clearTimeout(dragEndTimer);
            dragEndTimer = null;
        }
    }
    function markConnectLike() {
        lastConnectLikeAt = performance.now();
    }
    function isConnectLikeRecent() {
        return performance.now() - lastConnectLikeAt < CONNECT_SUPPRESS_MS;
    }

    function cancelCodeGen() {
        if (codeGenTimer) {
            clearTimeout(codeGenTimer);
            codeGenTimer = null;
        }
    }

    function scheduleCodeGen() {
        cancelCodeGen();
        codeGenTimer = setTimeout(() => {
            if (isDisposingRef.current) return;

            const xml = Blockly.Xml.workspaceToDom(ws);
            let variablesEl = xml.querySelector('variables');
            if (!variablesEl) {
                variablesEl = xml.ownerDocument.createElement('variables');
                xml.insertBefore(variablesEl, xml.firstChild);
            }
            const varMap = ws.getVariableMap();
            for (const variable of varMap.getAllVariables()) {
                const v = variable as unknown as { getId(): string; name: string; type: string };
                const alreadyExists = Array.from(variablesEl.children).some(
                    (el) => el.getAttribute('id') === v.getId(),
                );
                if (!alreadyExists) {
                    const varEl = xml.ownerDocument.createElement('variable');
                    varEl.setAttribute('type', v.type);
                    varEl.setAttribute('id', v.getId());
                    varEl.textContent = v.name;
                    variablesEl.appendChild(varEl);
                }
            }

            const existingFunctionsEl = xml.querySelector('functions');
            if (existingFunctionsEl) existingFunctionsEl.remove();
            const functions = useBlockFunctionStore.getState().functions;
            if (functions.length > 0) {
                const functionsEl = xml.ownerDocument.createElement('functions');
                functionsEl.textContent = JSON.stringify(functions);
                xml.appendChild(functionsEl);
            }

            const xmlText = Blockly.Xml.domToText(xml);
            const { currentFileId, updateFile, markAsDirty } = useBoardEditorFileStore.getState();
            if (!currentFileId) return;
            const generatedCode = generateArduinoCode(ws);
            updateFile(currentFileId, { blockContent: xmlText, codeContent: generatedCode, isBlockSynced: true });
            useBoardEditorStore.getState().setCode(generatedCode);
            markAsDirty(currentFileId);
            codeGenTimer = null;
        }, CODE_GEN_DEBOUNCE_MS);
    }

    const listener = function (e: Blockly.Events.Abstract) {
        if (!e.isUiEvent) {
            (ws as any).undoStack_ = [];
            (ws as any).redoStack_ = [];
        }
        if (!e.isUiEvent && e.type !== Blockly.Events.FINISHED_LOADING && e.type !== Blockly.Events.VIEWPORT_CHANGE) {
            const { currentFileId } = useBoardEditorFileStore.getState();
            if (currentFileId && !isLoadingRef.current) scheduleCodeGen();
        }
        const group = (e as any).group as string | undefined;
        if (e.type === Blockly.Events.VIEWPORT_CHANGE) {
            setBlockZoomScale(ws.getScale());
            applyFlyoutLayout(ws);
            forceFlyoutBackgroundHardRect(ws);
            return;
        }
        if (e.type === 'selected') {
            const blockId = (e as any).newElementId as string | undefined;
            if (blockId && ws.getBlockById(blockId)) captureDeleteCandidate(ws, blockId);
            return;
        }
        if (e.type === 'var_delete') {
            const deletedVarName = (e as any).varName as string | undefined;
            if (!deletedVarName) return;

            for (const block of [...ws.getAllBlocks(false)]) {
                if (block.type === 'func') {
                    const params: { id: string; type: string }[] = (block as any)._params ?? [];
                    params.forEach((p, i) => {
                        const field = block.getField(`PARAM_${p.id}`) as any;
                        if (field?.getValue() === deletedVarName) {
                            field.setVarName(`引数${i + 1}`);
                            (block as Blockly.BlockSvg).render();
                        }
                    });
                }
                if (['var_get_number', 'var_get_string', 'var_get_bool'].includes(block.type)) {
                    const varLabel = block.getField('VAR_LABEL')?.getValue();
                    if (varLabel === deletedVarName) {
                        block.dispose(true);
                    }
                }
                if (['var_set_number', 'var_set_string', 'var_set_bool', 'var_increment_number'].includes(block.type)) {
                    const field = block.getField('VAR_LABEL') as any;
                    if (field?.getValue() === deletedVarName) {
                        field.setVarName('設定する変数');
                        (block as Blockly.BlockSvg).render();
                    }
                }
            }
            return;
        }
        if (e.type === Blockly.Events.BLOCK_DRAG) {
            const isStart = !!(e as any).isStart;
            const blockId = (e as any).blockId as string | undefined;
            if (isStart) {
                isDragging = true;
                window.dispatchEvent(new CustomEvent('blockly-flyout-close'));
                if (!isConnectLikeRecent()) safePlay('drag-start', `drag-start:${group ?? ''}`, 140);
                captureDeleteCandidate(ws, blockId);
                return;
            }
            isDragging = false;
            requestAnimationFrame(() => {
                if (!ws || isDisposingRef.current) return;
                try {
                    const b = blockId ? (ws.getBlockById(blockId) as any) : null;
                    b?.unselect?.();
                    (Blockly as any).common?.setSelected?.(null);
                } catch {}
            });
            cancelDragEnd();
            dragEndTimer = setTimeout(() => {
                if (!isConnectLikeRecent()) safePlay('drag-end', `drag-end:${group ?? ''}`, 100);
                dragEndTimer = null;
            }, DRAG_END_DELAY_MS);
            return;
        }
        if (e.type === Blockly.Events.BLOCK_DELETE) {
            if (isDisposingRef.current) return;
            if ((e as Blockly.Events.BlockDelete as any).wasShadow) return;
            cancelDragEnd();
            safePlay('delete', `delete:${group ?? ''}`, 80);
            triggerDeleteEffect();
            validateI2CDevices(ws);
            return;
        }
        if (e.type === Blockly.Events.BLOCK_MOVE) {
            const moveEvent = e as Blockly.Events.BlockMove;
            const movedBlockId = (moveEvent as any).blockId as string | undefined;

            if (movedBlockId) {
                validateFuncReturnBlocksUnder(ws, movedBlockId);
                validateLoopControlBlocksUnder(ws, movedBlockId);
                validateBoardBlocksUnder(ws, movedBlockId);
                validateDeviceNameBlocksUnder(ws, movedBlockId);
            }
            validateI2CDevices(ws);

            const oldParent = (moveEvent as any).oldParentId;
            const newParent = (moveEvent as any).newParentId;
            if (!oldParent && newParent) {
                if (!isShadowSoundSuppressed()) {
                    cancelDragEnd();
                    markConnectLike();
                    safePlay('connect', `connect:${group ?? ''}:${newParent}`, 90);
                }
                return;
            }
            if (oldParent && !newParent) {
                if (!isShadowSoundSuppressed()) {
                    cancelDragEnd();
                    markConnectLike();
                    safePlay('disconnect', `disconnect:${group ?? ''}:${oldParent}`, 90);
                }
                return;
            }
        }
    };

    return { listener, cancelCodeGen, cancelDragEnd };
}

export default function BoardBlockEditor() {
    const blocklyRef = useRef<HTMLDivElement>(null);
    const workspaceRef = useRef<Blockly.WorkspaceSvg | null>(null);
    const [workspace, setWorkspace] = useState<Blockly.WorkspaceSvg | null>(null);
    const isLoadingRef = useRef(false);
    const isDisposingRef = useRef(false);
    const changeListenerRef = useRef<((e: Blockly.Events.Abstract) => void) | null>(null);
    const codeGenCancelRef = useRef<(() => void) | null>(null);
    const dragEndCancelRef = useRef<(() => void) | null>(null);
    const prevFunctionsRef = useRef<FunctionDef[]>([]);

    const isDark = useBoardEditorConfigStore((s) => s.isDark);
    const setBlockZoomScale = useBoardEditorStore((s) => s.setBlockZoomScale);
    const blockZoomScale = useBoardEditorStore((s) => s.blockZoomScale);
    const editorMode = useBoardEditorStore((s) => s.editorMode);
    const board = useBoardEditorStore((s) => s.board);
    const currentFileId = useBoardEditorFileStore((s) => s.currentFileId);
    const files = useBoardEditorFileStore((s) => s.files);
    const updateFile = useBoardEditorFileStore((s) => s.updateFile);
    const functions = useBlockFunctionStore((s) => s.functions);

    const currentFile = files.find((f) => f.id === currentFileId);
    const isBlockSynced = currentFile?.isBlockSynced ?? true;

    useEffect(() => {
        const ws = workspaceRef.current;
        if (!ws) return;

        const prevIds = new Set(prevFunctionsRef.current.map((f) => f.id));
        const currentIds = new Set(functions.map((f) => f.id));
        const deletedIds = [...prevIds].filter((id) => !currentIds.has(id));

        if (deletedIds.length > 0) {
            Blockly.Events.disable();
            try {
                for (const block of [...ws.getAllBlocks(false)]) {
                    if (block.type === 'func' || block.type === 'func_return') {
                        const fnId: string = (block as any)._fnId ?? '';
                        if (deletedIds.includes(fnId)) {
                            block.dispose(true);
                        }
                    } else if (block.type === 'func_call') {
                        const fnId: string = (block as any)._fnId ?? '';
                        if (deletedIds.includes(fnId)) {
                            const field = block.getField('FN_LABEL') as any;
                            field?.setFnName('作成した関数');
                            (block as any)._fnId = '';
                            (block as any)._fnName = '作成した関数';
                            (block as any)._params = [];
                            const xml = Blockly.utils.xml.createElement('mutation');
                            xml.setAttribute('fn_id', '');
                            xml.setAttribute('fn_name', '作成した関数');
                            xml.setAttribute('params', '[]');
                            (block as any).domToMutation(xml);
                            (block as Blockly.BlockSvg).render();
                        }
                    }
                }
            } finally {
                Blockly.Events.enable();
            }
        }

        for (const block of [...ws.getAllBlocks(false)]) {
            if (block.type !== 'func' && block.type !== 'func_return' && block.type !== 'func_call') continue;

            const fnId: string = (block as any)._fnId ?? '';
            if (!fnId) continue;

            const fn = functions.find((f) => f.id === fnId);
            if (!fn) continue;

            (block as any)._fnName = fn.name;

            if (block.type === 'func') {
                const prevParams: FunctionParam[] = (block as any)._params ?? [];
                const paramsChanged =
                    prevParams.length !== fn.params.length || prevParams.some((p, i) => p.type !== fn.params[i]?.type);

                if (paramsChanged) {
                    const savedVarNames: Record<string, string> = {};
                    prevParams.forEach((p, i) => {
                        const field = block.getField(`PARAM_${p.id}`) as any;
                        const val = field?.getValue();
                        const newParam = fn.params[i];
                        if (val && val !== `引数${i + 1}` && newParam && newParam.type === p.type) {
                            savedVarNames[p.id] = val;
                        }
                    });

                    const xml = Blockly.utils.xml.createElement('mutation');
                    xml.setAttribute('fn_id', fn.id);
                    xml.setAttribute('fn_name', fn.name);
                    xml.setAttribute('params', JSON.stringify(fn.params));
                    (block as any).domToMutation(xml);

                    fn.params.forEach((p) => {
                        if (savedVarNames[p.id]) {
                            const field = block.getField(`PARAM_${p.id}`) as any;
                            field?.setVarName(savedVarNames[p.id]);
                        }
                    });

                    (block as Blockly.BlockSvg).render();
                } else {
                    const nameField = block.getField('FN_NAME');
                    if (nameField && nameField.getValue() !== fn.name) {
                        (nameField as any).setValue(fn.name);
                        (block as Blockly.BlockSvg).render();
                    }
                }
            }

            if (block.type === 'func_return') {
                const nameField = block.getField('FN_NAME');
                const expected = ` ${fn.name} を終了して`;
                if (nameField && nameField.getValue() !== expected) {
                    (nameField as any).setValue(expected);
                    (block as Blockly.BlockSvg).render();
                }

                const currentReturnType = (block as any)._returnType ?? 'float';
                const newReturnType = fn.returnType ?? null;

                if (currentReturnType !== newReturnType) {
                    if (!newReturnType) {
                        Blockly.Events.disable();
                        try {
                            block.dispose(true);
                        } finally {
                            Blockly.Events.enable();
                        }
                    } else {
                        const xml = Blockly.utils.xml.createElement('mutation');
                        xml.setAttribute('fn_id', fn.id);
                        xml.setAttribute('fn_name', fn.name);
                        xml.setAttribute('return_type', newReturnType);
                        (block as any).domToMutation(xml);
                        (block as Blockly.BlockSvg).render();
                    }
                }
            }

            if (block.type === 'func_call') {
                const prevParams: FunctionParam[] = (block as any)._params ?? [];
                const paramsChanged =
                    prevParams.length !== fn.params.length || prevParams.some((p, i) => p.type !== fn.params[i]?.type);
                const currentReturnType = (block as any)._returnType ?? null;
                const returnTypeChanged = currentReturnType !== (fn.returnType ?? null);

                if (paramsChanged || returnTypeChanged) {
                    const xml = Blockly.utils.xml.createElement('mutation');
                    xml.setAttribute('fn_id', fn.id);
                    xml.setAttribute('fn_name', fn.name);
                    xml.setAttribute('params', JSON.stringify(fn.params));
                    (block as any).domToMutation(xml);
                    (block as Blockly.BlockSvg).render();
                } else {
                    const fnLabelField = block.getField('FN_LABEL');
                    if (fnLabelField && fnLabelField.getValue() !== fn.name) {
                        (fnLabelField as any).setValue(fn.name);
                        (block as Blockly.BlockSvg).render();
                    }
                }
            }
        }

        prevFunctionsRef.current = functions;
    }, [functions]);

    const initWorkspace = useCallback(() => {
        if (!blocklyRef.current || workspaceRef.current || isDisposingRef.current) return;
        Blockly.setLocale(Ja as unknown as Record<string, string>);
        Object.keys(Blockly.Msg).forEach((key) => {
            if (Blockly.Msg[key] === '実行') Blockly.Msg[key] = '';
        });
        const ws = Blockly.inject(blocklyRef.current, {
            theme: schoomyTheme,
            renderer: 'schoomy',
            toolbox: emptyFlyout,
            collapse: false,
            sounds: false,
            grid: { spacing: 40, length: 40, colour: '#ffffff', snap: true },
            zoom: { controls: false, wheel: true, startScale: 0.8, maxScale: 3, minScale: 0.3, scaleSpeed: 1.1 },
            trashcan: false,
            move: { scrollbars: true, drag: true, wheel: true },
        } as any);

        (Blockly as any).config.snapRadius = 60;
        (Blockly as any).config.connectingSnapRadius = 60;

        blocklyRef.current?.addEventListener(
            'keydown',
            (e) => {
                if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y')) {
                    e.stopPropagation();
                    e.preventDefault();
                }
            },
            true,
        );

        patchFlyoutNoDelete(ws);
        initBlockMenu();

        registerNumberPreset(ws);
        registerStringPreset(ws);
        registerBoolPreset(ws);

        const { listener, cancelCodeGen, cancelDragEnd } = buildChangeListener(
            ws,
            isLoadingRef,
            isDisposingRef,
            setBlockZoomScale,
        );
        ws.addChangeListener(listener);
        changeListenerRef.current = listener;
        codeGenCancelRef.current = cancelCodeGen;
        dragEndCancelRef.current = cancelDragEnd;
        if (blocklyRef.current) initBlockParticle(blocklyRef.current);
        workspaceRef.current = ws;
        setWorkspace(ws);
        setBlockZoomScale(ws.getScale());
        blockSound.preload();
    }, [setBlockZoomScale]);

    useEffect(() => {
        const el = blocklyRef.current;
        if (!el) return;
        if (isDark) {
            el.classList.add('blockly-dark');
        } else {
            el.classList.remove('blockly-dark');
        }
        const gridLines = el.querySelectorAll<SVGLineElement>('svg pattern line');
        gridLines.forEach((line) => {
            line.setAttribute('stroke', isDark ? '#dadada' : '#ffffff');
        });
        const mainBg = el.querySelector<SVGRectElement>('.blocklyMainBackground');
        if (mainBg) mainBg.setAttribute('fill', isDark ? '#111111' : '#ffffff');
    }, [isDark]);

    useEffect(() => {
        const handler = () => {
            const ws = workspaceRef.current;
            if (!ws) return;
            const file = useBoardEditorFileStore.getState().getCurrentFile();
            if (!file) return;
            isLoadingRef.current = true;
            loadWorkspace(ws, file.blockContent, useBoardEditorStore.getState().board);
            const generatedCode = generateArduinoCode(ws);
            const { currentFileId, updateFile } = useBoardEditorFileStore.getState();
            if (currentFileId) {
                updateFile(currentFileId, { codeContent: generatedCode });
                useBoardEditorStore.getState().setCode(generatedCode);
            }
            requestAnimationFrame(() => {
                Blockly.svgResize(ws);
            });
            setTimeout(() => {
                isLoadingRef.current = false;
            }, 100);
        };
        window.addEventListener('blockly-restore', handler);
        return () => window.removeEventListener('blockly-restore', handler);
    }, []);

    useEffect(() => {
        isDisposingRef.current = false;
        initWorkspace();
        requestAnimationFrame(() => {
            const el = blocklyRef.current;
            if (!el) return;
            const currentIsDark = useBoardEditorConfigStore.getState().isDark;
            if (currentIsDark) el.classList.add('blockly-dark');
            const gridLines = el.querySelectorAll<SVGLineElement>('svg pattern line');
            gridLines.forEach((line) => {
                line.setAttribute('stroke', currentIsDark ? '#dadada' : '#ffffff');
            });
            const mainBg = el.querySelector<SVGRectElement>('.blocklyMainBackground');
            if (mainBg) mainBg.setAttribute('fill', currentIsDark ? '#1a1a1a' : '#ffffff');
        });
        return () => {
            isDisposingRef.current = true;
            if (changeListenerRef.current && workspaceRef.current) {
                workspaceRef.current.removeChangeListener(changeListenerRef.current);
                changeListenerRef.current = null;
            }
            dragEndCancelRef.current?.();
            dragEndCancelRef.current = null;
            codeGenCancelRef.current?.();
            codeGenCancelRef.current = null;
            const onError = (e: ErrorEvent) => {
                if (e.message?.includes('focus unregistered tree')) e.preventDefault();
            };
            window.addEventListener('error', onError);
            try {
                workspaceRef.current?.dispose();
            } catch {}
            workspaceRef.current = null;
            setWorkspace(null);
            disposeBlockParticle();
            setTimeout(() => window.removeEventListener('error', onError), 500);
        };
    }, [initWorkspace]);

    useEffect(() => {
        if (editorMode === 'code') {
            codeGenCancelRef.current?.();
        }
    }, [editorMode]);

    useEffect(() => {
        if (editorMode !== 'block') return;
        const ws = workspaceRef.current;
        if (!ws) return;
        const file = useBoardEditorFileStore.getState().getCurrentFile();
        if (!file) return;
        let cancelled = false;
        document.fonts.ready.then(() => {
            if (cancelled || !workspaceRef.current) return;
            const currentBoard = useBoardEditorStore.getState().board;
            loadWorkspace(ws, file.blockContent, currentBoard);
            requestAnimationFrame(() => Blockly.svgResize(ws));
        });
        return () => {
            cancelled = true;
        };
    }, [editorMode]);

    useEffect(() => {
        const ws = workspaceRef.current;
        if (!ws || !currentFileId || isChangingBoardFromBlock()) return;
        const currentFile = useBoardEditorFileStore.getState().files.find((f) => f.id === currentFileId);
        if (!currentFile) return;
        let cancelled = false;
        isLoadingRef.current = true;
        document.fonts.ready.then(() => {
            if (cancelled || !workspaceRef.current || isDisposingRef.current) return;
            loadWorkspace(ws, currentFile.blockContent, useBoardEditorStore.getState().board);
            setTimeout(() => {
                isLoadingRef.current = false;
            }, 100);
        });
        return () => {
            cancelled = true;
        };
    }, [currentFileId]);

    useEffect(() => {
        const ws = workspaceRef.current;
        if (!ws || isChangingBoardFromBlock()) return;
        syncAllBoardBlocks(ws, board, false);
    }, [board]);

    useEffect(() => {
        if (!blocklyRef.current || !workspaceRef.current) return;
        const ws = workspaceRef.current;
        const ro = new ResizeObserver(() => {
            if (useBoardEditorStore.getState().editorMode !== 'block') return;
            Blockly.svgResize(ws);
        });
        ro.observe(blocklyRef.current);
        return () => ro.disconnect();
    }, [workspace]);

    useEffect(() => {
        const handler = (e: Event) => {
            const ws = workspaceRef.current;
            if (!ws) return;
            const xml = (e as CustomEvent).detail?.xml as string | undefined;
            if (!xml) return;

            isLoadingRef.current = true;
            loadWorkspace(ws, xml, useBoardEditorStore.getState().board);

            requestAnimationFrame(() => {
                Blockly.svgResize(ws);
                ws.render();

                for (const block of ws.getAllBlocks(false)) {
                    (block as Blockly.BlockSvg).render();
                }

                ws.scrollCenter();
            });

            setTimeout(() => {
                isLoadingRef.current = false;
            }, 100);
        };
        window.addEventListener('blockly-load-xml', handler);
        return () => window.removeEventListener('blockly-load-xml', handler);
    }, []);

    useEffect(() => {
        if (!workspace) return;
        const handler = (e: Blockly.Events.Abstract) => {
            if (e.type !== 'click') return;
            if ((e as any).targetType === 'workspace') window.dispatchEvent(new CustomEvent('blockly-flyout-close'));
        };
        workspace.addChangeListener(handler);
        return () => workspace.removeChangeListener(handler);
    }, [workspace]);

    const handleRestoreConfirm = () => {
        const file = useBoardEditorFileStore.getState().getCurrentFile();
        if (file && currentFileId) {
            const blockContent = file.preEditBlockContent || file.blockContent;
            updateFile(currentFileId, {
                blockContent,
                isBlockSynced: true,
                isCodeEditConfirmed: false,
                preEditBlockContent: '',
            });
        }
        useBoardEditorStore.getState().setEditorMode('block');
        window.dispatchEvent(new CustomEvent('blockly-restore'));
    };

    return (
        <div className="absolute inset-0 flex flex-col">
            <div className="flex flex-1 min-h-0 relative">
                <BoardBlockEditorCategory workspace={workspace} />
                <div ref={blocklyRef} className={`flex-1 relative blockly-root ${isDark ? 'blockly-dark' : ''}`} />
                {!isBlockSynced && <div className="absolute inset-0 z-10" style={{ cursor: 'not-allowed' }} />}
                {!isBlockSynced && <BoardBlockSyncBanner onConfirm={handleRestoreConfirm} />}
                <BlockModal />
            </div>
            <BoardBlockEditorControls workspaceRef={workspaceRef} isDark={isDark} blockZoomScale={blockZoomScale} />
        </div>
    );
}
